// createIdbOutbox — общий движок персистентных очередей доставки поверх idb-keyval.
//
// Зачем. Очередей две: аудио-чанки записи лекции (`recording-upload-queue.ts`) и
// текстовые сообщения репетитора (`tutor-message-outbox.ts`). Скелет фонового
// дренажа у них совпадал почти посимвольно, но очередь сообщений успела обрасти
// защитами, которых у очереди чанков не было:
//   • межвкладочная блокировка (`navigator.locks`) — иначе две вкладки дренят
//     одну IndexedDB одновременно;
//   • фильтр владельца — после смены аккаунта чужие элементы уходили заново;
//   • online через пробу (`resolveOnlineStatus`), а не `navigator.onLine`,
//     который врёт в captive portal и залипает в false после SW-кэша.
// Движок вынесен сюда, поэтому обе очереди получают все три автоматически.
//
// Что остаётся специфичным для очереди (передаётся конфигом): порядок доставки,
// статусы/попытки, реакция на transient-ошибку, что считать permanent, и
// строгий ли FIFO (сообщения — да, чанки — нет).
//
// Отдельная БД на очередь — обязательна: idb-keyval не умеет два object store в
// одной БД без ручного version bump («object store was not found»).

import type { NetworkPort } from "@studyhub/platform/ports/network.port";
import { createStore, del, get, keys, set, type UseStore } from "idb-keyval";
import { resolveOnlineStatus, subscribeOnlineStatus } from "@/lib/network-status";
import { IS_CAPACITOR } from "@/lib/platform-detect";

/** Результат одной волны дренажа — очередь решает, что с ним делать. */
export interface DrainSummary<T> {
	/** Успешно доставленные (уже удалены из хранилища). */
	readonly sent: readonly T[];
	/** Выброшенные из-за permanent-ошибки — ВИДИМАЯ потеря, не молчаливая. */
	readonly dropped: readonly { item: T; err: unknown }[];
	/** Осталось в очереди после волны. */
	readonly remaining: number;
}

export interface IdbOutboxConfig<T> {
	/** Имя IndexedDB (своё на очередь — см. шапку файла). */
	dbName: string;
	storeName: string;
	/** Префикс ключей внутри store — отделяет элементы от служебных записей. */
	keyPrefix: string;
	/** Ключ элемента БЕЗ префикса. */
	keyOf: (item: T) => string;
	/** Имя web-lock для сериализации дренажа между вкладками. */
	lockName: string;
	/** Период фонового дренажа. */
	drainIntervalMs?: number;

	/** Владелец элемента. Элементы других владельцев не читаются и не отправляются. */
	ownerOf?: (item: T) => string | undefined;
	/** Текущий владелец очереди (userId). */
	userId?: string;

	/** Порядок доставки (FIFO по умолчанию задаёт очередь). */
	compare: (a: T, b: T) => number;

	/**
	 * Починка элемента при чтении (например, зависший `sending` после kill →
	 * снова `queued`). Вернуть новый объект — движок перезапишет его в IDB;
	 * вернуть тот же — оставит как есть; `null` — пропустить элемент.
	 */
	hydrate?: (item: T) => T | null;
	/** Пропустить элемент в этой волне (не ошибка, просто не его очередь). */
	skip?: (item: T) => boolean;

	/** Одна попытка доставки. */
	send: (item: T) => Promise<void>;
	/** true = сервер элемент не примет никогда → выбросить. */
	isPermanentError: (err: unknown) => boolean;
	/** Перед отправкой (пометить `sending`). */
	beforeSend?: (item: T) => Promise<void>;
	/** Временная ошибка: элемент остаётся в очереди (пометить `failed`, attempts++). */
	onTransientError?: (item: T, err: unknown) => Promise<void>;
	/** Строгий FIFO: не отправлять следующие, пока текущий не прошёл. */
	stopOnTransientError?: boolean;

	/** Число элементов в очереди изменилось. */
	onCountChange?: (count: number) => void;
	/** Волна дренажа завершилась. */
	onDrained?: (summary: DrainSummary<T>) => void | Promise<void>;
}

export interface IdbOutbox<T> {
	put: (item: T) => Promise<void>;
	getByKey: (key: string) => Promise<T | undefined>;
	/** Частичное обновление элемента (с проверкой владельца). */
	patch: (key: string, patch: Partial<T>) => Promise<T | null>;
	remove: (key: string) => Promise<void>;
	/** Удалить все элементы, чей ключ (без префикса) подошёл под предикат. */
	removeWhere: (predicate: (key: string) => boolean) => Promise<number>;
	/** Ключи элементов БЕЗ префикса (без фильтра владельца — это сырой уровень). */
	rawKeys: () => Promise<string[]>;
	/** Элементы владельца, отсортированные `compare`, после `hydrate`. */
	list: () => Promise<T[]>;
	count: (predicate?: (item: T) => boolean) => Promise<number>;
	/** Одна волна доставки. Reentrancy-safe + межвкладочный lock + online-проба. */
	drain: () => Promise<void>;
	/** Перечитать счётчик из IDB → `onCountChange`. */
	refreshCount: () => Promise<void>;
	/** Online-listener + интервал + recovery-drain. Возвращает cleanup. */
	startBackground: () => () => void;
}

const DEFAULT_DRAIN_INTERVAL_MS = 30_000;

/**
 * Подписка на появление сети. Capacitor — native network-порт (dynamic import
 * через переменную-specifier под гардом, rules/capacitor-platform.md); web —
 * общая проба `network-status`, тот же источник, что у UI и onlineManager.
 */
async function subscribeOnline(handler: () => void): Promise<() => void> {
	if (IS_CAPACITOR) {
		try {
			const moduleId = "@studyhub/platform/adapters/capacitor/network.capacitor";
			const mod = (await import(moduleId)) as { networkCapacitorAdapter: NetworkPort };
			return mod.networkCapacitorAdapter.onChange((online) => {
				if (online) handler();
			});
		} catch {
			// адаптер недоступен — общий web-путь ниже
		}
	}
	return subscribeOnlineStatus((online) => {
		if (online) handler();
	});
}

/** Дренаж под web-lock: две вкладки не дренят одну очередь одновременно. */
async function withDrainLock(lockName: string, run: () => Promise<void>): Promise<void> {
	const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
	if (locks?.request) {
		try {
			await locks.request(lockName, { ifAvailable: true }, async (lock) => {
				// lock=null — дренит другая вкладка, её волна доставит и наши элементы.
				if (!lock) return;
				await run();
			});
			return;
		} catch {
			// locks API недоступен / упал — деградируем до дренажа без блокировки
		}
	}
	await run();
}

export function createIdbOutbox<T>(config: IdbOutboxConfig<T>): IdbOutbox<T> {
	const store: UseStore = createStore(config.dbName, config.storeName);
	const fullKey = (key: string): string => `${config.keyPrefix}${key}`;
	let draining = false;

	const safeKeys = async (): Promise<string[]> => {
		try {
			const all = await keys(store);
			return all.filter(
				(k): k is string => typeof k === "string" && k.startsWith(config.keyPrefix),
			);
		} catch {
			return [];
		}
	};

	const rawKeys = async (): Promise<string[]> =>
		(await safeKeys()).map((k) => k.slice(config.keyPrefix.length));

	const ownedByCurrentUser = (item: T): boolean => {
		if (!config.ownerOf) return true;
		// Владелец очереди ещё неизвестен (auth не догрузился) — скрывать нечего:
		// иначе `list`/`count` на холодном старте врут, что очередь пуста, и
		// нумерация чанков продолжается поверх недоставленных. Отправку в этом
		// состоянии блокирует `runDrain`, поэтому чужое всё равно не уйдёт.
		if (config.userId == null) return true;
		const owner = config.ownerOf(item);
		// Элемент без владельца — наследие до введения фильтра, отдаём текущему.
		if (owner == null) return true;
		return owner === config.userId;
	};

	const list = async (): Promise<T[]> => {
		const items: T[] = [];
		for (const key of await safeKeys()) {
			try {
				const raw = await get<T>(key, store);
				if (!raw || !ownedByCurrentUser(raw)) continue;
				const hydrated = config.hydrate ? config.hydrate(raw) : raw;
				if (!hydrated) continue;
				if (hydrated !== raw) await set(key, hydrated, store);
				items.push(hydrated);
			} catch {
				// битая запись — пропускаем, она не должна валить всю очередь
			}
		}
		return items.toSorted(config.compare);
	};

	const count = async (predicate?: (item: T) => boolean): Promise<number> => {
		const items = await list();
		return predicate ? items.filter(predicate).length : items.length;
	};

	const refreshCount = async (): Promise<void> => {
		if (!config.onCountChange) return;
		config.onCountChange(await count());
	};

	const remove = async (key: string): Promise<void> => {
		try {
			await del(fullKey(key), store);
		} catch {
			// нечего удалять / IDB недоступна
		}
	};

	const patch = async (key: string, part: Partial<T>): Promise<T | null> => {
		const cur = await get<T>(fullKey(key), store);
		if (!cur || !ownedByCurrentUser(cur)) return null;
		const next = { ...cur, ...part };
		await set(fullKey(key), next, store);
		return next;
	};

	/** Одна попытка по элементу. `false` = прервать волну (строгий FIFO). */
	const deliver = async (
		item: T,
		sent: T[],
		dropped: { item: T; err: unknown }[],
	): Promise<boolean> => {
		try {
			await config.beforeSend?.(item);
			await config.send(item);
			await remove(config.keyOf(item));
			sent.push(item);
			return true;
		} catch (err) {
			if (config.isPermanentError(err)) {
				await remove(config.keyOf(item));
				dropped.push({ item, err });
				return true;
			}
			await config.onTransientError?.(item, err);
			return !config.stopOnTransientError;
		}
	};

	const runDrain = async (): Promise<void> => {
		if (draining) return;
		draining = true;
		try {
			// Очередь с владельцем, но без известного текущего пользователя, отправлять
			// нельзя: неизвестно, чьи это элементы (смена аккаунта / логаут).
			if (config.ownerOf && config.userId == null) return;
			if (!(await resolveOnlineStatus())) return;
			const queued = await list();
			if (queued.length === 0) return;
			const sent: T[] = [];
			const dropped: { item: T; err: unknown }[] = [];
			for (const item of queued) {
				// Сеть пропала посреди волны — остальное доставит следующая.
				if (!(await resolveOnlineStatus())) break;
				if (config.skip?.(item)) continue;
				if (!(await deliver(item, sent, dropped))) break;
			}
			const remaining = await count();
			config.onCountChange?.(remaining);
			await config.onDrained?.({ sent, dropped, remaining });
		} finally {
			draining = false;
		}
	};

	const drain = (): Promise<void> => withDrainLock(config.lockName, runDrain);

	return {
		put: async (item) => {
			await set(fullKey(config.keyOf(item)), item, store);
			await refreshCount();
		},
		getByKey: (key) => get<T>(fullKey(key), store),
		patch,
		remove,
		removeWhere: async (predicate) => {
			let removed = 0;
			for (const key of await rawKeys()) {
				if (!predicate(key)) continue;
				await remove(key);
				removed += 1;
			}
			await refreshCount();
			return removed;
		},
		rawKeys,
		list,
		count,
		drain,
		refreshCount,
		startBackground: () => {
			let disposed = false;
			let unsubscribe: (() => void) | undefined;
			void subscribeOnline(() => void drain()).then((unsub) => {
				// Подписка резолвится асинхронно — если cleanup уже был, снимаем сразу.
				if (disposed) unsub();
				else unsubscribe = unsub;
			});
			const intervalId = setInterval(
				() => void drain(),
				config.drainIntervalMs ?? DEFAULT_DRAIN_INTERVAL_MS,
			);
			// Recovery: показать хвост прошлой сессии и сразу попробовать доставить.
			void refreshCount().then(() => {
				if (!disposed) void drain();
			});
			return () => {
				disposed = true;
				unsubscribe?.();
				clearInterval(intervalId);
			};
		},
	};
}
